import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import MasonryGrid from './MasonryGrid';
import FullscreenView from './FullscreenView';
import FilterSidebar from './FilterSidebar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PortfolioView() {
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [fullscreenProject, setFullscreenProject] = useState(null);
  const [fullscreenIndex, setFullscreenIndex] = useState(0);
  const [contact, setContact] = useState({ phone: '', email: '', address: '' });

  useEffect(() => {
    fetchData();
  }, [selectedCategory]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const categoryParam = selectedCategory ? `?category=${selectedCategory}` : '';
      const [projectsRes, categoriesRes, contactRes] = await Promise.all([
        axios.get(`${API}/projects${categoryParam}`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/contact`)
      ]);
      setProjects(projectsRes.data);
      setCategories(categoriesRes.data);
      setContact(contactRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProjectClick = (project, imageIndex = 0) => {
    setFullscreenProject(project);
    setFullscreenIndex(imageIndex);
  };

  const closeFullscreen = () => {
    setFullscreenProject(null);
    setFullscreenIndex(0);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="portfolio-view">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 glass border-b border-gray-100">
        <div className="max-w-[1800px] mx-auto px-6 py-5 flex items-center justify-between">
          <h1 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#1a1a1a' }} data-testid="portfolio-title">
            Architecture Studio
          </h1>
          <Button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="btn-hover"
            style={{ background: '#b8d71b', color: '#1a1a1a' }}
            data-testid="filter-toggle-btn"
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="pt-24 pb-12">
        <MasonryGrid
          projects={projects}
          loading={loading}
          onProjectClick={handleProjectClick}
        />
      </div>

      {/* Filter Sidebar */}
      <FilterSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        categories={categories}
        selectedCategory={selectedCategory}
        onCategorySelect={setSelectedCategory}
        contact={contact}
      />

      {/* Fullscreen View */}
      {fullscreenProject && (
        <FullscreenView
          project={fullscreenProject}
          imageIndex={fullscreenIndex}
          onClose={closeFullscreen}
          onIndexChange={setFullscreenIndex}
        />
      )}

      {/* Floating Action Button */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="fixed bottom-8 right-8 z-30 w-14 h-14 rounded-full shadow-lg btn-hover flex items-center justify-center md:hidden"
        style={{ background: '#b8d71b', color: '#1a1a1a' }}
        data-testid="floating-filter-btn"
      >
        <Menu size={24} />
      </button>
    </div>
  );
}